import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import { getPreloaderFnContext } from '@confluence/query-preloader-tools/entry-points/preloaderFnContext.ts';
import {
	parseCount,
	parseStyle,
	resolveSpaceKey,
	toStyleEnum,
} from '@confluence/popular-labels-macro/entry-points/popularLabelsParamsUtils.ts';
import {
	usePopularLabelsDataQuery,
	type usePopularLabelsDataQuery$data,
	type usePopularLabelsDataQuery$variables,
} from '@confluence/popular-labels-macro/entry-points/usePopularLabelsDataQuery.ts';
import {
	usePopularLabelsDataSpaceIdQuery,
	type usePopularLabelsDataSpaceIdQuery$data,
	type usePopularLabelsDataSpaceIdQuery$variables,
} from '@confluence/popular-labels-macro/entry-points/usePopularLabelsDataSpaceIdQuery.ts';

import type {
	MacroContentNode,
	MacroPreloadContext,
	MacroPreloadHandler,
} from '../preloadMacroData.ts';

/**
 * Resolves a `spaceKey` to its `spaceId` by preloading the popular-labels
 * macro's own space-id query. This is the same query (`space(key:) { id }`)
 * that `usePopularLabelsDataForSpace` runs on the client, so the response
 * warms the same Apollo cache entry the renderer reads from.
 *
 * Returns `null` when the space cannot be resolved.
 */
const preloadSpaceId = async (spaceKey: string): Promise<string | null> => {
	const result = await query<
		usePopularLabelsDataSpaceIdQuery$data,
		usePopularLabelsDataSpaceIdQuery$variables
	>({
		query: usePopularLabelsDataSpaceIdQuery,
		variables: { spaceKey },
		context: { single: true },
	});
	return result?.data?.space?.id ?? null;
};

/**
 * Preload handler for the popular-labels macro.
 *
 * When the macro author has supplied an explicit `spaceKey` macro param we
 * first preload `usePopularLabelsDataSpaceIdQuery` to translate
 * `spaceKey → spaceId`, then preload `usePopularLabelsDataQuery` scoped to
 * that space. Both caches are warmed so the renderer's `useQueryInterop`
 * calls return synchronously on the client.
 *
 * The two queries are necessarily sequential because the popular-labels
 * query depends on the resolved spaceId.
 */
export const popularLabelsPreloadHandler: MacroPreloadHandler = {
	extensionKey: 'popular-labels',

	routes: ['view', 'space-overview', 'edit', 'live'],

	preload: async (node: MacroContentNode, context: MacroPreloadContext): Promise<void> => {
		try {
			const { cloudId } = getPreloaderFnContext();
			if (!cloudId) {
				return;
			}

			const macroParams = node.attrs?.parameters?.macroParams;
			// Resolve the legacy `'currentSpace()'` sentinel against the host
			// page's space key before issuing the space-id lookup, so the
			// preloaded space-id cache entry matches the renderer's lookup.
			const spaceKeyParam = resolveSpaceKey(macroParams?.spaceKey?.value, context.spaceKey);

			let spaceId: string | null = null;
			if (spaceKeyParam) {
				spaceId = await preloadSpaceId(spaceKeyParam);
				if (!spaceId) {
					// SpaceKey didn't resolve — skip preloading the labels query rather
					// than warming the wrong (global) scope. The renderer will surface
					// the same error on the client.
					return;
				}
			}

			const variables: usePopularLabelsDataQuery$variables = {
				cloudId,
				first: parseCount(macroParams?.count?.value),
				style: toStyleEnum(parseStyle(macroParams?.style?.value)),
				spaceId,
			};

			await query<usePopularLabelsDataQuery$data, usePopularLabelsDataQuery$variables>({
				query: usePopularLabelsDataQuery,
				variables,
				context: { single: true },
			});
		} catch {
			// Preloading is best-effort cache warming. Ignore failures here and
			// let the renderer perform the query in its normal error-handled path.
			return;
		}
	},
};
